import { useMemo } from 'react';

import { getDevDocsFolderMetadata } from '../DevDocsFolderMetadata.ts';
import type { DevDocsContentPropertyNode } from '../DevDocsProperties.ts';
import { useDevDocsFolderEligibility } from '../folder-eligibility/DevDocsFolderEligibility.ts';
import { isDevDocsOwnedFolder } from '../folder-eligibility/DevDocsFolderOwnership.ts';

const MANAGED_FOLDER_SUBTYPE = 'managed';

export type DevDocsBylineConnectionAncestor = {
	readonly id?: string | null;
	readonly title?: string | null;
	readonly type?: string | null;
	readonly subType?: string | null;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- GraphQL content field, not browser history.
	readonly history?: {
		readonly createdBy?: {
			readonly displayName?: string | null;
		} | null;
	} | null;
	readonly links?: {
		readonly webui?: string | null;
	} | null;
	readonly properties?: {
		readonly nodes?: readonly DevDocsContentPropertyNode[] | null;
	} | null;
} | null;

export type DevDocsBylineConnection = {
	readonly folderId: string;
	readonly folderWebui?: string | null;
	readonly repoName: string;
	readonly provider: string | null;
	readonly owner: string | null;
	readonly lastSynced: string | null;
};

const isManagedFolderAncestor = (ancestor: DevDocsBylineConnectionAncestor): boolean =>
	ancestor?.type?.toLowerCase() === 'folder' &&
	ancestor?.subType?.toLowerCase() === MANAGED_FOLDER_SUBTYPE;

const getCreatedByDisplayName = (ancestor: DevDocsBylineConnectionAncestor): string | null => {
	const displayName = ancestor?.['history']?.createdBy?.displayName?.trim();
	return displayName || null;
};

export const getDevDocsConnectionFromAncestors = (
	ancestors?: readonly DevDocsBylineConnectionAncestor[] | null,
): DevDocsBylineConnection | null => {
	const managedDevDocsAncestor = ancestors?.find((ancestor) => {
		if (!isManagedFolderAncestor(ancestor)) {
			return false;
		}

		if (!isDevDocsOwnedFolder({ propertyCollections: [ancestor?.properties?.nodes] })) {
			return false;
		}

		return Boolean(
			getDevDocsFolderMetadata(ancestor?.properties?.nodes).devDocsProperties?.repoName,
		);
	});

	const folderMetadata = getDevDocsFolderMetadata(managedDevDocsAncestor?.properties?.nodes);
	const repoName = folderMetadata.devDocsProperties?.repoName;
	const folderId = managedDevDocsAncestor?.id;

	if (!repoName || !folderId) {
		return null;
	}

	return {
		folderId,
		folderWebui: managedDevDocsAncestor?.links?.webui ?? null,
		repoName,
		provider: folderMetadata.containerApp,
		owner: getCreatedByDisplayName(managedDevDocsAncestor),
		lastSynced: folderMetadata.smartFolderProperties?.lastSynced ?? null,
	};
};

export const useDevDocsBylineConnection = (
	ancestors?: readonly DevDocsBylineConnectionAncestor[] | null,
): DevDocsBylineConnection | null => {
	const ancestorPropertyCollections = useMemo(
		() => ancestors?.map((ancestor) => ancestor?.properties?.nodes) ?? [],
		[ancestors],
	);
	const { isDevDocsEligible } = useDevDocsFolderEligibility({
		propertyCollections: ancestorPropertyCollections,
	});

	return useMemo(() => {
		if (!isDevDocsEligible) {
			return null;
		}

		return getDevDocsConnectionFromAncestors(ancestors);
	}, [ancestors, isDevDocsEligible]);
};
